/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useState } from 'react'
import { useRef } from 'react'

import { Alert, Autocomplete, Card, Checkbox, Fade, Icon, Snackbar, styled } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { useLocation, useNavigate } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import MDBox from 'components/MDBox'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { storage } from 'config/firebase'
import { ROLE_LEVEL } from 'constants/roles'
import { useAuth } from 'context/auth'
import {
	setBusinessAccounts,
	setOrgMembers,
	updateRoutes,
	useMaterialUIController,
} from 'context/materialUI'
import { FeedbackTypes } from 'context/materialUI/types'
import ChangeLog from 'modules/ChangeLog'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import ManageCard from 'modules/ManageCard'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import { ROUTE_PATHS, RouteType } from 'routes'
import { fetchFeedbackItems } from 'utils/feedback'
import { formatEnumLabel } from 'utils/string'

import CommendationForm from './components/CommendationForm'
import ComplaintForm from './components/ComplaintForm'
import { FormContext, INIT_FEEDBACK } from './constants'
import { Feedback } from './types'
import { FEEDBACK_TYPE_OPTIONS } from '../constants'
import { AnyFile, RemoteFile } from '../types'
import { getFilenameFromFirebaseUrl } from '../utils'

const PrintableBox = styled(MDBox)(({ theme }) => ({
	'@media print': {
		marginTop: theme.spacing(5),
		marginRight: theme.spacing(2),
		marginLeft: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
}))

const StyledMenuCard = styled(Card)(() => ({
	height: '5rem',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'center',
}))

function FeedbackPage(): JSX.Element {
	const contentRef = useRef<HTMLDivElement>(null)
	const reactToPrintFn = useReactToPrint({ contentRef })

	const [controller, dispatch] = useMaterialUIController()
	const { configuration, routes } = controller
	const { user } = useAuth()

	const navigate = useNavigate()
	const location = useLocation()
	const queryClient = useQueryClient()

	const locationPathNameParts = location.pathname.split('/')
	const feedbackId = locationPathNameParts[locationPathNameParts.length - 1]

	const [localForm, setLocalForm] = useState<Feedback>(INIT_FEEDBACK)
	const [formEdited, setFormEdited] = useState<boolean>(false)
	const [showError, setShowError] = useState<boolean>(false)

	const [snackbarMessages, setSnackbarMessages] = useState<{ msg: string; open: boolean }[]>([])

	const orgMembers = configuration?.organisation.memberTypes.map(m => m.name) ?? []

	const isNew = feedbackId === 'new' ? true : false

	const pushSnackbarMessage = (message: string) => {
		setSnackbarMessages(prev => [...prev, { msg: message, open: true }])
	}

	const fetchFeedback = async () => {
		const response = await axios.get<Feedback>(`${process.env.REACT_APP_API_URL}/feedback`, {
			params: {
				feedback_id: feedbackId,
			},
			headers: {
				Authorization: await user.auth.getIdToken(),
			},
		})
		return response.data
	}

	const uploadFile = async (file: AnyFile, path: string): Promise<string> => {
		if (file.type === 'local') {
			const storageRef = ref(storage, `${path}/${file.file.name}`)
			await uploadBytes(storageRef, file.file)
			return await getDownloadURL(storageRef)
		}
		return file.url
	}

	const deleteFileFromStorage = async (file: AnyFile) => {
		if (file.type === 'remote' && file.delete) {
			const fileRef = ref(storage, file.url)
			await deleteObject(fileRef)
			return null
		}
		return file
	}

	const handleRemoteAttachmentsDeletion = async (files: AnyFile[]) => {
		if (!files || files.length === 0) return []

		const results = await Promise.allSettled(files.map(file => deleteFileFromStorage(file)))
		const fulfilledRemoteAttachments = results
			.map((result, index) => {
				if (result.status === 'fulfilled') {
					return result.value
				} else {
					const rejectedFile = files[index]
					const rejectedFileName =
						rejectedFile.type === 'local'
							? rejectedFile.file.name
							: getFilenameFromFirebaseUrl(rejectedFile.url)
					pushSnackbarMessage(`Failed to delete file ${rejectedFileName}`)
					return files[index]
				}
			})
			.filter(file => file)

		return fulfilledRemoteAttachments
	}

	const handleAttachmentUploads = async (files: AnyFile[], newFeedbackId?: string) => {
		if (!files || files.length === 0) return []

		const path = `laurel/${user.data.organisation.id}/feedback/${
			newFeedbackId ?? localForm.form.details.id
		}`

		const results = await Promise.allSettled(files.map(file => uploadFile(file, path)))
		const fulfilledRemoteAttachments = results
			.map((result, index) => {
				if (result.status === 'fulfilled') {
					const remoteFile: RemoteFile = {
						type: 'remote',
						url: result.value,
					}
					return remoteFile
				} else {
					const rejectedFile = files[index]
					const rejectedFileName =
						rejectedFile.type === 'local'
							? rejectedFile.file.name
							: getFilenameFromFirebaseUrl(rejectedFile.url)
					setLocalForm(prev => ({
						...prev,
						form: {
							...prev.form,
							details: {
								...prev.form.details,
								attachments: prev.form.details.attachments.filter(
									attachment => attachment !== rejectedFile
								),
							},
						},
					}))
					pushSnackbarMessage(`Failed to upload file ${rejectedFileName}`)
					return null
				}
			})
			.filter(url => url)

		return fulfilledRemoteAttachments
	}

	const saveNewFeedback = async () => {
		const localFormWithoutAttachments: Feedback = {
			...localForm,
			form: {
				...localForm.form,
				details: {
					...localForm.form.details,
					attachments: [],
				},
			},
		}

		const response = await axios.post<{ id: string }>(
			`${process.env.REACT_APP_API_URL}/feedback`,
			localFormWithoutAttachments,
			{
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const deleteFeedback = async () => {
		await axios.delete(`${process.env.REACT_APP_API_URL}/feedback`, {
			params: {
				feedback_id: localForm.form.details.id,
			},
			headers: {
				Authorization: await user.auth.getIdToken(),
			},
		})
	}

	const updateFeedback = async (newAttachmentUrls: RemoteFile[]) => {
		const localFormWithAttachmentUrls: Feedback = {
			...localForm,
			form: {
				...localForm.form,
				details: {
					...localForm.form.details,
					attachments: newAttachmentUrls,
				},
			},
		}
		const response = await axios.put<Feedback>(
			`${process.env.REACT_APP_API_URL}/feedback`,
			localFormWithAttachmentUrls,
			{
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const addAttachmentsToNewlyCreatedFeedback = async (
		newAttachmentUrls: RemoteFile[],
		newFeedbackId: string
	) => {
		const localFormWithAttachments: Feedback = {
			...localForm,
			form: {
				...localForm.form,
				details: {
					...localForm.form.details,
					id: newFeedbackId,
					attachments: newAttachmentUrls,
				},
			},
		}

		const response = await axios.patch<Feedback>(
			`${process.env.REACT_APP_API_URL}/feedback`,
			localFormWithAttachments,
			{
				headers: {
					Authorization: await user.auth.getIdToken(),
				},
			}
		)
		return response.data
	}

	const {
		data: feedbackData,
		isLoading: isFeedbackLoading,
		error: feedbackError,
		refetch: feedbackRefetch,
	} = useQuery({
		queryKey: [`feedback-${feedbackId}`],
		queryFn: fetchFeedback,
		enabled: !isNew,
	})
	const { data: itemsData, refetch: itemsRefetch } = useQuery({
		queryKey: ['feedback-items', feedbackId],
		queryFn: () => fetchFeedbackItems(user.auth),
	})

	useEffect(() => {
		if (!isNew && feedbackData) {
			setLocalForm(feedbackData)
		} else {
			setShowError(false)
			setLocalForm(INIT_FEEDBACK)
		}
	}, [feedbackData, isNew])

	useEffect(() => {
		if (itemsData) {
			setOrgMembers(dispatch, itemsData.orgMembers)
			setBusinessAccounts(dispatch, itemsData.businessAccounts)
		}
	}, [itemsData, dispatch])

	useEffect(() => {
		if (snackbarMessages.length > 0 && snackbarMessages.every(snackbar => !snackbar.open)) {
			const timeout = setTimeout(() => {
				setSnackbarMessages([])
			}, 500)
			return () => clearTimeout(timeout)
		}
	}, [snackbarMessages])

	const feedbackOptionsLocked = useMemo(
		() => !isNew && feedbackData?.type.toUpperCase() !== 'CUSTOMER_FEEDBACK', // Disable changing feedback type if editing existing feedback or if customer feedback
		[isNew, feedbackData]
	)

	return (
		<FormContext.Provider
			value={{ localForm, setLocalForm, formEdited, setFormEdited, isNew, showError, setShowError }}
		>
			<DashboardLayout>
				<DashboardNavbar isMini />
				{snackbarMessages.map((snackbar, index) => (
					<Snackbar
						key={index}
						open={snackbar.open}
						autoHideDuration={5000}
						onClose={() =>
							setSnackbarMessages(prev =>
								prev.map((item, i) => (i === index ? { ...item, open: false } : item))
							)
						}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						TransitionComponent={Fade}
						style={{
							bottom: `${20 + index * 60}px`,
							right: '20px',
							position: 'fixed',
						}}
					>
						<Alert severity="error" variant="standard" sx={{ width: '100%' }}>
							{snackbar.msg}
						</Alert>
					</Snackbar>
				))}

				<MDBox mt={4}>
					<Grid container spacing={3}>
						<Grid item xs={12} lg={9}>
							{isNew || (feedbackData && !isFeedbackLoading && !feedbackError) ? (
								<PrintableBox
									mb={3}
									display={'flex'}
									flexDirection="column"
									gap={3}
									alignItems={'center'}
									ref={contentRef}
								>
									<MDBox display="flex" flexDirection="column" alignItems="center">
										<MDTypography variant="h2">
											{isNew ? 'New' : 'Edit:'} {formatEnumLabel(localForm.type)}{' '}
											{isNew ? '' : `#${localForm.form.details.id}`}
										</MDTypography>
										{feedbackData?.type === 'CUSTOMER_FEEDBACK' && (
											<MDTypography
												variant="caption"
												fontStyle="italic"
												alignItems="center"
												display="flex"
											>
												<Icon fontSize="small">info_outline</Icon>
												&nbsp;Please update customer feedback to type: complaint or commendation
											</MDTypography>
										)}
									</MDBox>
									{localForm.type === 'COMPLAINT' ? <ComplaintForm /> : <CommendationForm />}
								</PrintableBox>
							) : (
								<LoadingComponent type="box" />
							)}
						</Grid>
						<Grid item xs={12} lg={3}>
							{isNew || (feedbackData && !isFeedbackLoading && !feedbackError) ? (
								<Grid container spacing={3}>
									<Grid item xs={12}>
										<ManageCard
											formEdited={formEdited}
											setFormEdited={setFormEdited}
											save={{
												disabled: user.data.role === 'VIEWER',
												onClicked: async () => {
													try {
														if (isNew) {
															const newFeedback = await saveNewFeedback()
															const downloadUrls = await handleAttachmentUploads(
																localForm.form.details.attachments,
																newFeedback.id
															)
															if (localForm.form.details.attachments.length > 0) {
																await addAttachmentsToNewlyCreatedFeedback(
																	downloadUrls,
																	newFeedback.id
																)
															}
															const newFeedbackRoute: RouteType[] = [
																{
																	type: 'invisible',
																	name: `${localForm.type}: ${newFeedback.id}`,
																	key: `${localForm.type}-${newFeedback.id}`,
																	route: `${ROUTE_PATHS.FEEDBACK.BASE}/${newFeedback.id}`,
																	component: <FeedbackPage />,
																	authRequired: true,
																},
															]
															updateRoutes(dispatch, routes, newFeedbackRoute)
															navigate(`${ROUTE_PATHS.FEEDBACK.BASE}/${newFeedback.id}`)
														} else {
															const postDeletionAttachments = await handleRemoteAttachmentsDeletion(
																localForm.form.details.attachments
															)
															const downloadUrls =
																await handleAttachmentUploads(postDeletionAttachments)
															await updateFeedback(downloadUrls)
															feedbackRefetch()
															itemsRefetch()
														}
														setShowError(false)
													} catch (error: any) {
														setShowError(true)
														throw error
													}
												},
											}}
											print={{ onClicked: reactToPrintFn }}
											deleteItem={{
												visible: !isNew,
												onClicked: async () => {
													await deleteFeedback()
													queryClient.invalidateQueries(['feedback-table'])
													navigate(ROUTE_PATHS.FEEDBACK.TABLE)
													updateRoutes(
														dispatch,
														routes,
														routes.filter(r => r.key !== `feedback-${feedbackId}`)
													)
												},
											}}
										/>
									</Grid>
									<Grid item xs={12}>
										<StyledMenuCard>
											<MDTypography variant="h6" color="secondary">
												Feedback Type:&nbsp;&nbsp;&nbsp;
											</MDTypography>
											<Autocomplete
												disableClearable
												value={formatEnumLabel(localForm.type)}
												options={FEEDBACK_TYPE_OPTIONS.map(f => formatEnumLabel(f))}
												readOnly={feedbackOptionsLocked}
												onChange={(_, newValue) => {
													setLocalForm(prev => ({
														...prev,
														type: newValue.toUpperCase() as FeedbackTypes,
													}))
												}}
												size="medium"
												sx={{ width: '10rem' }}
												renderInput={params => <MDInput {...params} />}
											/>
										</StyledMenuCard>
									</Grid>
									<Grid item xs={12}>
										<StyledMenuCard
											style={{
												height: '5rem',
												flexDirection: 'row',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											<MDTypography variant="h6" color="secondary">
												Member Type:&nbsp;&nbsp;&nbsp;
											</MDTypography>

											<Autocomplete
												disableClearable
												value={localForm.memberType === '' ? null : localForm.memberType}
												options={orgMembers}
												readOnly={feedbackOptionsLocked}
												noOptionsText={'No member types set'}
												onChange={(_, newValue) => {
													setLocalForm(prev => ({
														...prev,
														memberType: newValue,
														form: {
															...prev.form,
															details: {
																...prev.form.details,
																orgMember: INIT_FEEDBACK.form.details.orgMember,
															},
														},
													}))
												}}
												size="medium"
												sx={{ width: '10rem' }}
												renderInput={params => <MDInput {...params} />}
											/>
										</StyledMenuCard>
									</Grid>
									{ROLE_LEVEL[2].includes(user.data.role) && !isNew && (
										<Grid item xs={12}>
											<StyledMenuCard>
												<Grid container justifyContent={'center'} alignItems={'center'} mx={3}>
													<Grid item xs={9}>
														<MDTypography variant="h6" color="secondary">
															Admin Approval:
														</MDTypography>
													</Grid>
													<Grid item xs={3}>
														<Checkbox
															checked={localForm.stage.current >= 6}
															onChange={() => {
																setLocalForm(prev => ({
																	...prev,
																	stage: {
																		...prev.stage,
																		current: localForm.stage.current >= 6 ? 6 - 1 : 6,
																	},
																}))
																setFormEdited(true)
															}}
														/>
													</Grid>
												</Grid>
											</StyledMenuCard>
										</Grid>
									)}
									{!isNew && (
										<Grid item xs={12}>
											<ChangeLog
												log={feedbackData.changeLog} // Placeholder for change log
											/>
										</Grid>
									)}
								</Grid>
							) : (
								<LoadingComponent type="box" height={'30rem'} />
							)}
						</Grid>
					</Grid>
				</MDBox>
			</DashboardLayout>
		</FormContext.Provider>
	)
}

export default FeedbackPage
